import { useMemo, useState } from "react";

import Portal from "components/layout/Portal";
import { ReactComponent as BottomRightCornerIcon } from "assets/svgs/navigation/map/bottomrightcorner.svg";
import { ReactComponent as DownTriangleIcon } from "assets/svgs/DownTriangle.svg";
import { useFloatingPopup } from "hooks/useFloatingPopup";
import { useNavLogAutoScroll } from "hooks/useNavLogAutoScroll";
import DevWebrtcHistoryBar from "components/control/DevWebrtcHistoryBar";
import DevWebrtcStatsCharts from "components/control/DevWebrtcStatsCharts";
import { formatBytes, formatTime } from "lib/webrtc/debugFormat";
import { GENERAL_LOG, type DebugLogEntry } from "lib/webrtc/debugLogStore";
import type { StreamStatsSnapshot } from "lib/webrtc/debugStats";
import { useWebRTCDebug } from "providers/WebRTCDebugProvider";

const NAL_LABELS: Record<number, string> = { 7: "SPS", 8: "PPS" };

const ALL_ARCS = "__all__";
const UNKNOWN_ARC = "__unknown__";

const arcLabel = (entry: DebugLogEntry): string => entry.arcSerial ?? "—";

const arcKey = (entry: DebugLogEntry): string => entry.arcSerial ?? UNKNOWN_ARC;

const describeEntry = (entry: DebugLogEntry): string => {
    switch (entry.kind) {
        case "nal": {
            const label = NAL_LABELS[entry.nalType as number] ?? `NAL ${entry.nalType}`;
            if (entry.event === "unchanged") {
                return `${label} unchanged ×${entry.repeats}`;
            }
            const profile = entry.profileLevelId
                ? ` ${entry.profileLevelId}${entry.profileName ? ` ${entry.profileName}` : ""} L${entry.level}`
                : "";
            return `${label} ${entry.event} ${entry.size} B${profile}\n    ${entry.hex}`;
        }
        case "connection":
            return `connection ${entry.phase} ${entry.connectionId}`;
        case "warning":
            return `warning: ${entry.message}`;
        case "reconnect":
            if (entry.phase === "scheduled") {
                return `reconnect attempt ${entry.attempt} in ${entry.delayMs} ms — ${entry.reason}`;
            }
            if (entry.phase === "start") return `reconnect attempt ${entry.attempt} started`;
            return `reconnect gave up after ${entry.attempt} attempts — ${entry.reason}`;
        case "signaling":
            return `signaling ${entry.phase}${entry.detail ? ` — ${entry.detail}` : ""}`;
        case "producer":
            return `producer ${entry.phase} ${entry.producerId ?? ""}`;
        case "stall": {
            const reasons = Array.isArray(entry.reasons) ? ` (${entry.reasons.join(", ")})` : "";
            return `STALL ${entry.cause}${reasons}`;
        }
        default:
            return JSON.stringify(entry);
    }
};

const StatsTable: React.FC = () => {
    const { streams: allStreams, stats, currentConnectionOnly, setCurrentConnectionOnly } = useWebRTCDebug();
    const streams = allStreams.filter((stream) => stream !== GENERAL_LOG);
    if (streams.length === 0) return null;

    const cell = (snapshot: StreamStatsSnapshot | undefined, pick: (counters: StreamStatsSnapshot["current"]) => number, format: (n: number) => string) => {
        if (!snapshot) return "—";
        return format(pick(currentConnectionOnly ? snapshot.current : snapshot.total));
    };

    return (
        <div className="shrink-0 border-b border-black80 bg-black100/80 px-3 py-2 text-[11px]">
            <label className="mb-1 flex cursor-pointer items-center gap-2 text-h5 text-black40">
                <input
                    type="checkbox"
                    checked={currentConnectionOnly}
                    onChange={(event) => setCurrentConnectionOnly(event.target.checked)}
                />
                Current connection only
            </label>
            <table className="w-full font-mono tabular-nums">
                <thead>
                    <tr className="text-left text-black60">
                        <th className="pr-2 font-normal">Stream</th>
                        <th className="pr-2 text-right font-normal">Bytes</th>
                        <th className="pr-2 text-right font-normal">Received</th>
                        <th className="pr-2 text-right font-normal">Decoded</th>
                        <th className="pr-2 text-right font-normal">I</th>
                        <th className="pr-2 text-right font-normal">P</th>
                        <th className="pr-2 text-right font-normal">Size</th>
                        <th className="text-right font-normal">Reconn.</th>
                    </tr>
                </thead>
                <tbody>
                    {streams.map((stream) => {
                        const snapshot = stats.get(stream);
                        return (
                            <tr key={stream} className="text-white">
                                <td className="pr-2">{stream}</td>
                                <td className="pr-2 text-right">{cell(snapshot, (c) => c.bytesReceived, formatBytes)}</td>
                                <td className="pr-2 text-right">{cell(snapshot, (c) => c.framesReceived, String)}</td>
                                <td className="pr-2 text-right">{cell(snapshot, (c) => c.framesDecoded, String)}</td>
                                <td className="pr-2 text-right">{cell(snapshot, (c) => c.iFrames, String)}</td>
                                <td className="pr-2 text-right">{cell(snapshot, (c) => c.pFrames, String)}</td>
                                <td className="pr-2 text-right">
                                    {snapshot?.width && snapshot.height ? `${snapshot.width}×${snapshot.height}` : "—"}
                                </td>
                                <td className="text-right">{snapshot ? snapshot.reconnects : "—"}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const StreamCharts: React.FC = () => {
    const { stats, selectedStream } = useWebRTCDebug();
    if (selectedStream === GENERAL_LOG) return null;
    const history = (selectedStream && stats.get(selectedStream)?.history) || [];
    return (
        <div className="shrink-0 border-b border-black80 bg-black100/80">
            <DevWebrtcStatsCharts history={history} />
        </div>
    );
};

// Marks where a connection starts so everything below it belongs to that connection.
const ConnectionDivider: React.FC<{ entry: DebugLogEntry }> = ({ entry }) => {
    const ordinal = Number(entry.ordinal) || 1;
    const label = ordinal > 1 ? `Reconnection #${ordinal - 1}` : "Connection";
    return (
        <div className="my-2 flex items-center gap-2 text-black40" role="separator">
            <span className="h-px flex-1 bg-black70" />
            <span className="whitespace-nowrap">
                {label} · {formatTime(entry.ts)} · {arcLabel(entry)} · {String(entry.connectionId)}
            </span>
            <span className="h-px flex-1 bg-black70" />
        </div>
    );
};

type StreamLogProps = {
    entries: DebugLogEntry[];
    emptyText: string;
};

const StreamLog: React.FC<StreamLogProps> = ({ entries, emptyText }) => {
    const { storageError } = useWebRTCDebug();
    const lineKeys = useMemo(() => entries.map((entry) => `${entry.ts}-${entry.kind}`), [entries]);
    const { scrollRef, onScroll } = useNavLogAutoScroll(lineKeys, { resumeDelayMs: 7000 });

    return (
        <>
            {storageError ? <p className="shrink-0 px-3 py-1 text-h5 text-warning">Log storage: {storageError}</p> : null}
            <div
                ref={scrollRef}
                onScroll={onScroll}
                className="min-h-0 flex-1 overflow-y-auto bg-black100 p-3 font-mono text-[12px] leading-relaxed text-white"
            >
                {entries.length === 0 ? (
                    <p className="text-black40">{emptyText}</p>
                ) : (
                    entries.map((entry, index) => (entry.kind === "connection" && entry.phase === "open" ? (
                        <ConnectionDivider key={`${lineKeys[index]}-${index}`} entry={entry} />
                    ) : (
                        <div
                            key={`${lineKeys[index]}-${index}`}
                            className={`whitespace-pre-wrap break-words${entry.kind === "stall" || entry.phase === "exhausted" || entry.phase === "failed" ? " text-warning" : ""}`}
                        >
                            {entry.kind === "sdp" ? (
                                <details>
                                    <summary className="cursor-pointer">
                                        <span className="text-black60">{formatTime(entry.ts)}</span>{" "}
                                        <span className="text-black40">{arcLabel(entry)}</span>{" "}
                                        SDP {String(entry.origin)} {String(entry.type)} ({entry.connectionId})
                                    </summary>
                                    <pre className="mt-1 whitespace-pre-wrap text-black40">{String(entry.sdp)}</pre>
                                </details>
                            ) : (
                                <>
                                    <span className="text-black60">{formatTime(entry.ts)}</span>{" "}
                                    <span className="text-black40">{arcLabel(entry)}</span> {describeEntry(entry)}
                                </>
                            )}
                        </div>
                    )))
                )}
            </div>
        </>
    );
};

type StreamPillsProps = {
    streams: string[];
    selected: string | null;
    onSelect: (stream: string) => void;
};

const StreamPills: React.FC<StreamPillsProps> = ({ streams, selected, onSelect }) => (
    <div className="flex shrink-0 flex-wrap gap-1 border-b border-black80 bg-black100/80 px-3 py-1.5" role="tablist" aria-label="Stream">
        {streams.map((stream) => (
            <button
                key={stream}
                type="button"
                role="tab"
                aria-selected={stream === selected}
                onClick={() => onSelect(stream)}
                className={`rounded-full px-3 py-0.5 text-h5 ${
                    stream === selected ? "bg-primary2 font-semibold text-white" : "bg-black80 text-black40 hover:text-white"
                }`}
            >
                {stream}
            </button>
        ))}
    </div>
);

const LiveView: React.FC = () => {
    const { streams, selectedStream, setSelectedStream, entries } = useWebRTCDebug();
    return (
        <>
            <StatsTable />
            <StreamPills streams={streams} selected={selectedStream} onSelect={setSelectedStream} />
            <StreamCharts />
            <StreamLog
                entries={entries}
                emptyText={selectedStream === GENERAL_LOG ? "No general events yet." : "No entries yet for this stream."}
            />
        </>
    );
};

// Stored entries for a time range, filtered by the selected pill like the live view.
const HistoryView: React.FC = () => {
    const { history, arcSerial } = useWebRTCDebug();
    const [selected, setSelected] = useState<string | null>(null);
    const [chosenArc, setChosenArc] = useState<string | null>(null);

    const arcs = useMemo(() => [...new Set(history.entries.map(arcKey))].sort(), [history.entries]);
    // Defaults to the ARC the live view follows when it has entries in the range.
    const arc = chosenArc ?? (arcSerial && arcs.includes(arcSerial) ? arcSerial : ALL_ARCS);
    const arcEntries = useMemo(
        () => (arc === ALL_ARCS ? history.entries : history.entries.filter((entry) => arcKey(entry) === arc)),
        [history.entries, arc],
    );

    const streams = useMemo(
        () => [...new Set([GENERAL_LOG, ...arcEntries.map((entry) => entry.cameraKey ?? GENERAL_LOG)])],
        [arcEntries],
    );
    const active = selected && streams.includes(selected) ? selected : GENERAL_LOG;
    const entries = useMemo(
        () => arcEntries.filter((entry) => (entry.cameraKey ?? GENERAL_LOG) === active),
        [arcEntries, active],
    );

    return (
        <>
            <DevWebrtcHistoryBar />
            <div className="flex shrink-0 items-center gap-2 border-b border-black80 bg-black100/80 px-3 py-1.5 text-h5 text-black40">
                <label className="flex items-center gap-1">
                    ARC
                    <select
                        value={arc}
                        onChange={(event) => setChosenArc(event.target.value)}
                        className="rounded-r5 border border-black70 bg-black90 px-1 py-0.5 text-white"
                    >
                        <option value={ALL_ARCS}>All ARCs</option>
                        {arcs.map((key) => (
                            <option key={key} value={key}>{key === UNKNOWN_ARC ? "Unknown" : key}</option>
                        ))}
                    </select>
                </label>
            </div>
            <StreamPills streams={streams} selected={active} onSelect={setSelected} />
            <StreamLog entries={entries} emptyText="No stored entries for this stream in the selected range." />
        </>
    );
};

const DevWebrtcPopup: React.FC = () => {
    const { supported, enabled } = useWebRTCDebug();
    const [mode, setMode] = useState<"live" | "history">("live");
    const { geometry, collapsed, toggleCollapsed, panelRef, onDragHandlePointerDown, onResizePointerDown } =
        useFloatingPopup({ storageKey: "dev-webrtc-popup", defaultWidth: 560, defaultHeight: 720 });

    if (!supported || !enabled) {
        return null;
    }

    return (
        <Portal visible target={document.body}>
            <div
                ref={panelRef}
                className="pointer-events-auto fixed z-[120] flex min-w-[17rem] flex-col overflow-hidden rounded-r10 border border-black80 bg-black90 shadow-lg"
                style={{
                    left: geometry.x,
                    top: geometry.y,
                    width: geometry.w,
                    height: collapsed ? "auto" : geometry.h,
                }}
            >
                <div
                    className="flex shrink-0 cursor-grab touch-none select-none items-center gap-2 border-b border-black80 bg-black100/90 px-3 py-2 active:cursor-grabbing"
                    onPointerDown={onDragHandlePointerDown}
                >
                    <span className="text-h5 font-semibold text-white">WebRTC debug</span>
                    <div className="flex min-w-0 flex-1 items-center gap-1" role="tablist" aria-label="Log source">
                        {(["live", "history"] as const).map((candidate) => (
                            <button
                                key={candidate}
                                type="button"
                                role="tab"
                                aria-selected={mode === candidate}
                                onClick={() => setMode(candidate)}
                                className={`rounded-r5 px-2 py-0.5 text-h5 ${
                                    mode === candidate ? "bg-black80 font-semibold text-white" : "text-black40 hover:bg-black80/60 hover:text-white"
                                }`}
                            >
                                {candidate === "live" ? "Live" : "History"}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        aria-label={collapsed ? "Expand WebRTC debug" : "Collapse WebRTC debug"}
                        aria-expanded={!collapsed}
                        onClick={toggleCollapsed}
                        className="rounded-r5 border border-black70 bg-black90 p-0.5 text-black60 hover:bg-black80 hover:text-white"
                    >
                        <DownTriangleIcon className={`h-3.5 w-3.5 transition-transform${collapsed ? "" : " rotate-180"}`} />
                    </button>
                </div>

                {!collapsed && (
                    <>
                        {mode === "live" ? <LiveView /> : <HistoryView />}
                        <button
                            type="button"
                            aria-label="Resize WebRTC debug popup"
                            className="pointer-events-auto absolute bottom-0 right-0 z-[1] flex h-10 w-10 cursor-nwse-resize touch-none translate-x-1.5 translate-y-1.5 items-center justify-center p-0 text-black60 hover:text-primary2"
                            onPointerDown={onResizePointerDown}
                        >
                            <BottomRightCornerIcon className="pointer-events-none h-10 w-10 shrink-0 [&_path]:fill-current" aria-hidden />
                        </button>
                    </>
                )}
            </div>
        </Portal>
    );
};

export default DevWebrtcPopup;
